import { PATHS } from '@/router/paths';

// Map of route paths to their lazy-loaded chunks
const ROUTE_PREFETCH_MAP: Record<string, () => Promise<unknown>> = {
    [PATHS.HOME.INDEX]: () => import('@/pages/home/HomePage'),
    [PATHS.FLOOR_GUIDE.INDEX]: () => import('@/pages/floor-guide/FloorGuidePage'),
    [PATHS.SHOP.INDEX]: () => import('@/pages/shop/ShopPage'),
    [PATHS.FNB.INDEX]: () => import('@/pages/fnb/FNBPage'),
    [PATHS.EVENTS.INDEX]: () => import('@/pages/event/EventPage'),
    [PATHS.PARKING.INDEX]: () => import('@/pages/parking/ParkingPage'),
    [PATHS.FACILITY.INDEX]: () => import('@/pages/facility/FacilityPage'),
    [PATHS.FAQ.INDEX]: () => import('@/pages/faq/FAQPage'),
    [PATHS.TOUR_GUIDE.INDEX]: () => import('@/pages/tour-guide/TourGuidePage'),
};

// Track which routes have already been prefetched
const prefetchedRoutes = new Set<string>();

/**
 * Prefetch a route's lazy-loaded component
 * @param route - The route path to prefetch
 * @returns Promise that resolves when the component is loaded
 */
export const prefetchRoute = (route: string): Promise<void> => {
    // If already prefetched, skip
    if (prefetchedRoutes.has(route)) {
        return Promise.resolve();
    }

    const prefetchFn = ROUTE_PREFETCH_MAP[route];
    if (!prefetchFn) {
        return Promise.resolve();
    }

    // Mark as prefetching
    prefetchedRoutes.add(route);

    // Start prefetching with low priority
    return new Promise((resolve) => {
        // Use requestIdleCallback if available, otherwise setTimeout
        if ('requestIdleCallback' in window) {
            requestIdleCallback(
                () => {
                    prefetchFn()
                        .then(() => {
                            console.log(`[Prefetch] ✅ Prefetched route: ${route}`);
                            resolve();
                        })
                        .catch((error) => {
                            console.error(`[Prefetch] ❌ Failed to prefetch route: ${route}`, error);
                            // Remove from set so it can be retried
                            prefetchedRoutes.delete(route);
                            resolve();
                        });
                },
                { timeout: 2000 }
            );
        } else {
            setTimeout(() => {
                prefetchFn()
                    .then(() => {
                        console.log(`[Prefetch] ✅ Prefetched route: ${route}`);
                        resolve();
                    })
                    .catch((error) => {
                        console.error(`[Prefetch] ❌ Failed to prefetch route: ${route}`, error);
                        prefetchedRoutes.delete(route);
                        resolve();
                    });
            }, 100);
        }
    });
};

/**
 * Prefetch multiple routes
 * @param routes - Array of route paths to prefetch
 */
export const prefetchRoutes = (routes: string[]): void => {
    routes.forEach((route) => {
        prefetchRoute(route);
    });
};

/**
 * Clear the prefetch cache (useful for testing)
 */
export const clearPrefetchCache = (): void => {
    prefetchedRoutes.clear();
};
